// 카카오 공유 SDK 로더 (FE-02) — 앱 키·도메인 등록이 선행이므로 준비되지 않으면 아무것도 호출하지 않는다.
// 키는 환경변수 VITE_KAKAO_JS_KEY(JavaScript 키). 없으면 hasKakaoKey() = false → 공유 팝업의 카카오톡 타일이 비활성된다.
// 서버 API 호출 없음. SDK 스크립트 주소는 카카오 공식 CDN.

const KAKAO_SDK_SRC = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.4/kakao.min.js';

/** Kakao.Share.sendDefault 에 넘기는 기본 템플릿(feed) 설정 — 필요한 필드만 정의 */
export interface KakaoShareSettings {
  objectType: 'feed';
  content: {
    title: string;
    description?: string;
    imageUrl: string;
    link: { mobileWebUrl: string; webUrl: string };
  };
  buttons?: { title: string; link: { mobileWebUrl: string; webUrl: string } }[];
}

interface KakaoSdk {
  isInitialized(): boolean;
  init(key: string): void;
  Share: { sendDefault(settings: KakaoShareSettings): void };
}

declare global {
  interface Window {
    Kakao?: KakaoSdk;
  }
}

export interface KakaoShareInput {
  /** 공유할 고객 링크(서버가 준 고정 링크) */
  url: string;
  title: string;
  description?: string;
  /** 기본 템플릿(feed)은 이미지가 필수다. 템플릿 등록 여부는 구현 때 확인(결정 30) */
  imageUrl: string;
}

export function getKakaoJsKey(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return String(env?.VITE_KAKAO_JS_KEY ?? '').trim();
}

export function hasKakaoKey(): boolean {
  return getKakaoJsKey() !== '';
}

let loading: Promise<KakaoSdk> | null = null;

/** SDK 스크립트를 한 번만 불러와 초기화한다. 키가 없으면 거부. */
export function loadKakaoSdk(): Promise<KakaoSdk> {
  const key = getKakaoJsKey();
  if (!key) return Promise.reject(new Error('VITE_KAKAO_JS_KEY 가 설정되지 않았습니다.'));
  if (typeof window === 'undefined') return Promise.reject(new Error('브라우저 환경이 아닙니다.'));

  const ready = (): KakaoSdk => {
    const k = window.Kakao;
    if (!k) throw new Error('카카오 SDK 를 찾을 수 없습니다.');
    if (!k.isInitialized()) k.init(key);
    return k;
  };

  if (window.Kakao) return Promise.resolve().then(ready);
  if (!loading) {
    loading = new Promise<KakaoSdk>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = KAKAO_SDK_SRC;
      s.async = true;
      s.onload = () => {
        try { resolve(ready()); } catch (e) { loading = null; reject(e); }
      };
      s.onerror = () => {
        loading = null;
        s.remove();
        reject(new Error('카카오 SDK 를 불러오지 못했습니다.'));
      };
      document.head.appendChild(s);
    });
  }
  return loading;
}

/** SDK 가 준비된 경우에만 대화방 선택 화면을 연다(Kakao.Share.sendDefault). 준비 실패 시 거부 → 호출부가 안내. */
export async function shareViaKakao(input: KakaoShareInput): Promise<void> {
  const sdk = await loadKakaoSdk();
  const link = { mobileWebUrl: input.url, webUrl: input.url };
  sdk.Share.sendDefault({
    objectType: 'feed',
    content: { title: input.title, description: input.description, imageUrl: input.imageUrl, link },
    buttons: [{ title: '일정 보기', link }],
  });
}
